import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Loader2, RefreshCw, SwitchCamera, Check, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const hasCamera = () => typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;

/** Live camera dialog: preview → capture → retake / use. Returns a JPEG File via onCapture. */
export function CameraCapture({ open, onClose, onCapture, title = "Take a photo", maxSize = 1600 }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("environment");
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [shot, setShot] = useState(null); // { blob, url }
  const [multi, setMulti] = useState(false);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setReady(false);
  }, []);

  const start = useCallback(async (mode) => {
    stop(); setError("");
    if (!hasCamera()) { setError("This device has no camera we can use."); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: mode }, width: { ideal: 1280 }, height: { ideal: 960 } }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
      setReady(true);
      navigator.mediaDevices.enumerateDevices?.().then((ds) => setMulti(ds.filter((d) => d.kind === "videoinput").length > 1)).catch(() => {});
    } catch (e) {
      setError(e?.name === "NotAllowedError" ? "Camera access was blocked — allow it in your browser settings, or choose a photo instead." : "Could not start the camera.");
    }
  }, [stop]);

  useEffect(() => {
    if (open) { setShot(null); start(facing); } else { stop(); if (shot?.url) URL.revokeObjectURL(shot.url); setShot(null); }
    return stop;
  }, [open, facing]); // eslint-disable-line react-hooks/exhaustive-deps

  const capture = () => {
    const v = videoRef.current; if (!v || !v.videoWidth) return;
    const s = Math.min(1, maxSize / Math.max(v.videoWidth, v.videoHeight));
    const c = document.createElement("canvas");
    c.width = Math.round(v.videoWidth * s); c.height = Math.round(v.videoHeight * s);
    const ctx = c.getContext("2d");
    if (facing === "user") { ctx.translate(c.width, 0); ctx.scale(-1, 1); }
    ctx.drawImage(v, 0, 0, c.width, c.height);
    c.toBlob((blob) => { if (blob) { setShot({ blob, url: URL.createObjectURL(blob) }); stop(); } }, "image/jpeg", 0.88);
  };
  const retake = () => { if (shot?.url) URL.revokeObjectURL(shot.url); setShot(null); start(facing); };
  const use = () => {
    const file = new File([shot.blob], `photo-${new Date().toISOString().replace(/[:.]/g, "-")}.jpg`, { type: "image/jpeg" });
    onCapture(file); onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg p-4" data-testid="camera-capture-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Camera className="h-5 w-5" /> {title}</DialogTitle>
          <DialogDescription>{shot ? "Happy with it? Use the photo or retake." : "Frame the shot and tap the shutter."}</DialogDescription>
        </DialogHeader>
        <div className="relative overflow-hidden rounded-2xl bg-black aspect-[4/3]" data-testid="camera-preview">
          {shot ? <img src={shot.url} alt="Captured" className="h-full w-full object-contain" data-testid="camera-shot" />
            : <video ref={videoRef} playsInline muted className={`h-full w-full object-cover ${facing === "user" ? "-scale-x-100" : ""}`} />}
          {!shot && !ready && !error && <div className="absolute inset-0 flex items-center justify-center text-white/80"><Loader2 className="h-6 w-6 animate-spin" /></div>}
          {error && <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-white/90" data-testid="camera-error">{error}</div>}
          {!shot && ready && multi && (
            <button type="button" onClick={() => setFacing((f) => (f === "user" ? "environment" : "user"))} className="absolute right-3 top-3 rounded-full bg-black/50 p-2 text-white backdrop-blur" data-testid="camera-flip" aria-label="Switch camera"><SwitchCamera className="h-5 w-5" /></button>
          )}
        </div>
        <div className="flex items-center justify-center gap-3 pt-1">
          {shot ? (
            <>
              <button type="button" onClick={retake} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="camera-retake"><RefreshCw className="h-4 w-4" /> Retake</button>
              <button type="button" onClick={use} className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:shadow" data-testid="camera-use"><Check className="h-4 w-4" /> Use photo</button>
            </>
          ) : (
            <>
              <button type="button" onClick={onClose} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50" data-testid="camera-cancel"><X className="h-4 w-4" /> Cancel</button>
              <button type="button" onClick={capture} disabled={!ready} aria-label="Take photo" data-testid="camera-shutter"
                className="h-16 w-16 rounded-full border-4 border-white bg-primary shadow-lg ring-4 ring-primary/30 transition active:scale-95 disabled:opacity-40" />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
